import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import '../styles/Home.css';

const API_URL = process.env.REACT_APP_API_URL || '';

interface Testimonial {
  id: number;
  author_name: string;
  author_role: string;
  quote: string;
}

const Home: React.FC = () => {
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, testimonialsRes] = await Promise.all([
          axios.get(`${API_URL}/api/admin/settings`, {
            // we'll need to fetch public settings without JWT later,
            // but for now, we'll try to use a public endpoint if available,
            // or just hardcode fallbacks while loading
          }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/testimonials`)
        ]);

        const settingsMap: Record<string, string> = {};
        if (settingsRes.data && Array.isArray(settingsRes.data)) {
           settingsRes.data.forEach((s: any) => {
             settingsMap[s.key] = s.value;
           });
        }
        setSettings(settingsMap);
        setTestimonials(testimonialsRes.data);
      } catch (error) {
        console.error('Error fetching home data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fallbacks para caso a API falhe ou enquanto carrega
  const content = {
    heroTitle: settings['hero_title'] || 'Clínica Integrada de Saúde em Ji-Paraná',
    heroSubtitle: settings['hero_subtitle'] || 'Fisioterapia, Pilates, Estética e Massoterapia. Cuidamos de você com excelência e dedicação para uma vida mais saudável.',
    btnPrimary: settings['hero_btn_primary'] || 'Agende sua Consulta',
    btnSecondary: settings['hero_btn_secondary'] || 'Conheça Nossos Serviços',
    servicesTitle: settings['home_services_title'] || 'Nossos Serviços',
    servicesSubtitle: settings['home_services_subtitle'] || 'Oferecemos uma gama completa de serviços para atender às suas necessidades de saúde e bem-estar.',
    ctaTitle: settings['home_cta_title'] || 'Pronto para Cuidar de Você?',
    ctaSubtitle: settings['home_cta_subtitle'] || 'Agende sua consulta hoje mesmo e dê o primeiro passo para uma vida com mais saúde e bem-estar.'
  };

  return (
    <div className="home-page">
      <section className="hero-section">
        <div className="container">
          <div className="hero-content">
            <h1>{content.heroTitle}</h1>
            <p className="subtitle">{content.heroSubtitle}</p>
            <div className="hero-buttons">
              <Link to="/agendamento" className="btn btn-primary btn-lg">
                {content.btnPrimary}
              </Link>
              <Link to="/servicos" className="btn btn-outline btn-lg">
                {content.btnSecondary}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="section section-alt">
        <div className="container text-center">
          <h2>{content.servicesTitle}</h2>
          <p className="section-subtitle">{content.servicesSubtitle}</p>
          <div className="services-grid">
            <div className="service-item">
              <i className="fas fa-running"></i>
              <h3>Fisioterapia</h3>
              <p>Tratamentos personalizados para recuperação e prevenção de lesões.</p>
            </div>
            <div className="service-item">
              <i className="fas fa-dumbbell"></i>
              <h3>Pilates</h3>
              <p>Fortalecimento, flexibilidade e equilíbrio para todas as idades.</p>
              <Link to="/agendamento?service=Pilates - Aula Experimental" className="btn btn-sm btn-accent mt-2">
                Aula Experimental Grátis!
              </Link>
            </div>
            <div className="service-item">
              <i className="fas fa-spa"></i>
              <h3>Estética</h3>
              <p>Cuidados faciais e corporais para realçar sua beleza natural.</p>
            </div>
            <div className="service-item">
              <i className="fas fa-hand-holding-heart"></i>
              <h3>Massoterapia</h3>
              <p>Massagens terapêuticas para relaxamento e alívio de tensões.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <h2 className="text-center">Por Que Escolher a Clínica CIS?</h2>
          <div className="features-grid">
            <div className="feature-item">
              <i className="fas fa-user-md"></i>
              <h3>Profissionais Qualificados</h3>
              <p>Nossa equipe é composta por especialistas experientes e dedicados ao seu bem-estar.</p>
            </div>
            <div className="feature-item">
              <i className="fas fa-clinic-medical"></i>
              <h3>Ambiente Moderno</h3>
              <p>Equipamentos de ponta e um espaço acolhedor para seu conforto.</p>
            </div>
            <div className="feature-item">
              <i className="fas fa-hands-helping"></i>
              <h3>Atendimento Personalizado</h3>
              <p>Foco nas suas necessidades individuais para um tratamento eficaz.</p>
            </div>
            <div className="feature-item">
              <i className="fas fa-heart"></i>
              <h3>Resultados Comprovados</h3>
              <p>Ajuda comprovada na recuperação e melhoria da qualidade de vida.</p>
            </div>
          </div>
        </div>
      </section>

      {testimonials.length > 0 && (
        <section className="section section-alt">
          <div className="container">
            <h2 className="text-center">Depoimentos de Nossos Clientes</h2>
            <div className="testimonials-grid">
              {testimonials.map((t) => (
                <div className="testimonial-item" key={t.id}>
                  <p className="quote">"{t.quote}"</p>
                  <p className="author">
                    - {t.author_name} {t.author_role && <span className="author-role">({t.author_role})</span>}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="section call-to-action">
        <div className="container text-center">
          <h2>{content.ctaTitle}</h2>
          <p className="section-subtitle text-white">
            {content.ctaSubtitle}
          </p>
          <Link to="/agendamento" className="btn btn-accent btn-lg">
            Agendar Agora
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
